/**
 * Page layout for printable calendars: one month filling a page, and a year of twelve
 * small months on a page. Positions are millimetres on a page from `pageGeometry`; the
 * dates come from `calendar.ts`.
 */

import {
  type GridCell,
  monthGrid,
  rowIsoWeek,
  type WeekStart,
} from "@/lib/tool-kit/printables/calendar";
import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { r2 } from "@/lib/tool-kit/printables/page";

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DayCell extends GridCell {
  box: Box;
  /** The column's position in the week, 0 = the first day shown. */
  column: number;
  /** True for Saturday and Sunday. */
  weekend: boolean;
}

export interface WeekRow {
  box: Box;
  /** ISO 8601 week number for the row. */
  isoWeek: number;
  cells: DayCell[];
}

export interface MonthPage {
  year: number;
  month: number;
  /** Baseline of the month title. */
  titleBaseline: number;
  /** The header row of weekday names: one box per column. */
  header: Box[];
  /** The week-number column's x range, when shown. */
  weekColumn: { x: number; width: number } | null;
  rows: WeekRow[];
  notes: Box | null;
  grid: Box;
}

export interface MonthOptions {
  weekNumbers: boolean;
  notes: boolean;
}

const WEEK_COLUMN = 8;
const TITLE_BAND = 20;
const HEADER_ROW = 8;
const NOTES_SHARE = 0.2;

function isWeekend(weekdayIndex: number, weekStart: WeekStart): boolean {
  // Column c shows weekday (c + weekStart) % 7; Saturday is 6 and Sunday is 0.
  const dow = (weekdayIndex + weekStart) % 7;
  return dow === 0 || dow === 6;
}

/** One month, filling the content box: title band, weekday row, week rows, optional notes. */
export function layoutMonthPage(
  geometry: PageGeometry,
  year: number,
  month: number,
  weekStart: WeekStart,
  options: MonthOptions
): MonthPage {
  const weekWidth = options.weekNumbers ? WEEK_COLUMN : 0;
  const left = geometry.left;
  const gridLeft = left + weekWidth;
  const gridWidth = geometry.contentWidth - weekWidth;
  const columnWidth = gridWidth / 7;
  const headerTop = geometry.top + TITLE_BAND;
  const bodyTop = headerTop + HEADER_ROW;
  const bottom = geometry.top + geometry.contentHeight;
  const notesHeight = options.notes ? (bottom - bodyTop) * NOTES_SHARE : 0;
  const notesGap = options.notes ? 4 : 0;
  const gridBottom = bottom - notesHeight - notesGap;
  const weeks = monthGrid(year, month, weekStart);
  const rowHeight = (gridBottom - bodyTop) / weeks.length;

  const header: Box[] = Array.from({ length: 7 }, (_, column) => ({
    x: r2(gridLeft + column * columnWidth),
    y: r2(headerTop),
    width: r2(columnWidth),
    height: HEADER_ROW,
  }));
  const rows: WeekRow[] = weeks.map((week, index) => {
    const y = bodyTop + index * rowHeight;
    return {
      box: {
        x: r2(left),
        y: r2(y),
        width: r2(geometry.contentWidth),
        height: r2(rowHeight),
      },
      isoWeek: rowIsoWeek(week),
      cells: week.map((cell, column) => ({
        ...cell,
        column,
        weekend: isWeekend(column, weekStart),
        box: {
          x: r2(gridLeft + column * columnWidth),
          y: r2(y),
          width: r2(columnWidth),
          height: r2(rowHeight),
        },
      })),
    };
  });
  return {
    year,
    month,
    titleBaseline: r2(geometry.top + 12),
    header,
    weekColumn: options.weekNumbers ? { x: r2(left), width: weekWidth } : null,
    rows,
    notes: options.notes
      ? {
          x: r2(left),
          y: r2(gridBottom + notesGap),
          width: r2(geometry.contentWidth),
          height: r2(notesHeight),
        }
      : null,
    grid: {
      x: r2(gridLeft),
      y: r2(bodyTop),
      width: r2(gridWidth),
      height: r2(gridBottom - bodyTop),
    },
  };
}

/* ------------------------------------------------------------------- the year */

export interface MiniMonth {
  year: number;
  month: number;
  box: Box;
  /** Baseline of the month name. */
  nameBaseline: number;
  header: Box[];
  weekColumn: { x: number; width: number } | null;
  rows: WeekRow[];
}

export interface YearPage {
  /** Baseline of the title. */
  titleBaseline: number;
  months: MiniMonth[];
  /** Where the holiday list goes, under the months. */
  legend: Box | null;
}

export interface YearOptions {
  weekNumbers: boolean;
  legend: boolean;
  /** Months laid out across the page; four across in landscape, three in portrait. */
  columns: number;
}

const MINI_NAME = 7;
const MINI_HEADER = 4.5;
const MINI_GAP = 5;
const LEGEND_HEIGHT = 30;

/** Twelve months from `startMonth` of `startYear`, three or four across. */
export function layoutYearPage(
  geometry: PageGeometry,
  startYear: number,
  startMonth: number,
  weekStart: WeekStart,
  options: YearOptions
): YearPage {
  const titleBand = 16;
  const legendHeight = options.legend ? LEGEND_HEIGHT : 0;
  const top = geometry.top + titleBand;
  const bottom = geometry.top + geometry.contentHeight - legendHeight;
  const cols = options.columns;
  const rowsOfMonths = Math.ceil(12 / cols);
  const cellWidth = (geometry.contentWidth - MINI_GAP * (cols - 1)) / cols;
  const cellHeight =
    (bottom - top - MINI_GAP * (rowsOfMonths - 1)) / rowsOfMonths;
  const weekWidth = options.weekNumbers ? 4.5 : 0;
  const months: MiniMonth[] = Array.from({ length: 12 }, (_, index) => {
    const monthIndex = startMonth + index;
    const year = startYear + Math.floor(monthIndex / 12);
    const month = monthIndex % 12;
    const column = index % cols;
    const rowIndex = Math.floor(index / cols);
    const x = geometry.left + column * (cellWidth + MINI_GAP);
    const y = top + rowIndex * (cellHeight + MINI_GAP);
    const weeks = monthGrid(year, month, weekStart);
    const gridTop = y + MINI_NAME + MINI_HEADER;
    const dayWidth = (cellWidth - weekWidth) / 7;
    // Every mini month reserves six week rows so the months line up.
    const rowHeight = (y + cellHeight - gridTop) / 6;
    return {
      year,
      month,
      box: { x: r2(x), y: r2(y), width: r2(cellWidth), height: r2(cellHeight) },
      nameBaseline: r2(y + 5),
      header: Array.from({ length: 7 }, (_, d) => ({
        x: r2(x + weekWidth + d * dayWidth),
        y: r2(y + MINI_NAME),
        width: r2(dayWidth),
        height: MINI_HEADER,
      })),
      weekColumn: options.weekNumbers ? { x: r2(x), width: weekWidth } : null,
      rows: weeks.map((week, w) => ({
        box: {
          x: r2(x),
          y: r2(gridTop + w * rowHeight),
          width: r2(cellWidth),
          height: r2(rowHeight),
        },
        isoWeek: rowIsoWeek(week),
        cells: week.map((cell, d) => ({
          ...cell,
          column: d,
          weekend: isWeekend(d, weekStart),
          box: {
            x: r2(x + weekWidth + d * dayWidth),
            y: r2(gridTop + w * rowHeight),
            width: r2(dayWidth),
            height: r2(rowHeight),
          },
        })),
      })),
    };
  });
  return {
    titleBaseline: r2(geometry.top + 10),
    months,
    legend: options.legend
      ? {
          x: r2(geometry.left),
          y: r2(bottom + 3),
          width: r2(geometry.contentWidth),
          height: r2(legendHeight - 3),
        }
      : null,
  };
}
